import { createRouter } from '@tanstack/react-router';
import { type SentryDomain } from '@/api/errors';
import { LinearLoadingIndicator } from '@/components/Common/LinearLoadingIndicator';
import { DefaultNotFoundFallback } from '@/components/Error';
import { type RootRouteContext } from '@/router/index.types';
import { UnavailableAuth0Context } from '@/routes/__root.hooks';
import { routeTree } from '@/routeTree.gen';
import { routeMasks } from './routeMasks';

export const router = createRouter({
  context: {
    auth: UnavailableAuth0Context,
    queryClient: {},
    transport: {},
    statsigClient: {},
  } as RootRouteContext,
  // defaultPendingMinMs: 500,
  // defaultPreload: 'intent',
  defaultErrorComponent: undefined, // Use route-level error components only
  defaultNotFoundComponent: DefaultNotFoundFallback,
  // By default, TanStack Router will show a pending component for loaders that take longer than 1 second to resolve.
  defaultPendingComponent: LinearLoadingIndicator,
  defaultPreloadStaleTime: 0,
  // When disableGlobalCatchBoundary is true, this global error boundary is disabled, allowing errors to bubble up
  // to parent error boundaries or the browser's global error handlers
  disableGlobalCatchBoundary: true,
  routeMasks,
  routeTree,
  // Without it, every `router.invalidate()` after a mutation scrolls the page to the top.
  scrollRestoration: true,
});

/* eslint-disable @typescript-eslint/naming-convention */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }

  interface HistoryState {
    returnTo?: string;
  }

  interface StaticDataRouteOption {
    title?: string | string[];
    /**
     * Feature area that owns errors on this route subtree, used as the {@link SentryDomain} fallback
     * for events that don't originate at the QueryClient choke point (runtime errors, third-party
     * throws, direct-to-cluster fetches, …) and so carry no API-derived `domain`. Children inherit
     * the nearest annotated ancestor's value (see `getCurrentRouteDomain`); a child can override it.
     */
    domain?: SentryDomain;
  }
}
